import { present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';

import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideLetterSpacing } from '@kaiizen/kai-shiki/core';

// A single label/value row built purely from 2026 Kai Styleguide tokens so it
// can be promoted into kai-shiki (as a primitive, or a `layout: 'row'` variant
// of MetadataList) once it settles across the app. Labels and values are plain
// strings — the only real variation across the designs is label capitalization
// and value color (tone), so those are props rather than a ReactNode.

export type LabelValueTone = 'default' | 'positive' | 'muted';

const VALUE_TONE_COLORS: Record<LabelValueTone, string> = {
  default: styleguideColors.ink,
  positive: styleguideColors.success,
  muted: styleguideColors.textSecondary,
};

const ROW_STYLE = css`
  label: LABEL_VALUE_ROW;
  display: flex;
  gap: 10px;
  width: 100%;
  min-width: 0;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
`;

const INLINE_STYLE = css`
  label: LABEL_VALUE_ROW_INLINE;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const STACK_STYLE = css`
  label: LABEL_VALUE_ROW_STACK;
  flex-direction: column;
  gap: 2px;
`;

const LABEL_STYLE = css`
  label: LABEL_VALUE_ROW_LABEL;
  min-width: 0;
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
  color: ${styleguideColors.textSecondary};
  letter-spacing: ${styleguideLetterSpacing.monoLabel};
`;

const LABEL_UPPERCASE_STYLE = css`
  label: LABEL_VALUE_ROW_LABEL_UPPERCASE;
  text-transform: uppercase;
`;

const VALUE_STYLE = css`
  label: LABEL_VALUE_ROW_VALUE;
  min-width: 0;
`;

const VALUE_INLINE_STYLE = css`
  label: LABEL_VALUE_ROW_VALUE_INLINE;
  text-align: right;
`;

const PREVIOUS_VALUE_STYLE = css`
  label: LABEL_VALUE_ROW_PREVIOUS_VALUE;
  margin-right: 14px;
  color: ${styleguideColors.error};
  font-weight: ${styleguideFontWeight.regular};
`;

const UPDATE_LABEL_STYLE = css`
  label: LABEL_VALUE_ROW_UPDATE_LABEL;
  font-weight: ${styleguideFontWeight.regular};
`;

const VALUE_UPDATED_STYLE = css`
  label: LABEL_VALUE_ROW_VALUE_UPDATED;
  font-weight: ${styleguideFontWeight.bold};
`;

/** Presents a prior value as a "was …" note: `was 2`, or `was empty` when there was no value. */
export const presentWas = (previousValue: string | undefined) => `was ${present(previousValue) ? previousValue : 'empty'}`;

export interface LabelValueRowProps {
  label: string;
  value: string;
  /** UPPERCASE + wider tracking (Project Details / Access Info). Default: title-case (Activities). */
  uppercaseLabel?: boolean;
  /** Value color: default = ink, positive = success green (amounts), muted = secondary. */
  tone?: LabelValueTone;
  /** Optional prior value — rendered as `(was …)` in red, then `update: <value>` with the value bolded. Pass '' for "(was empty)". */
  previousValue?: string;
  /**
   * `inline` (default): label left, value right-aligned on one line.
   * `stack`: label above value — for multi-line content.
   */
  layout?: 'inline' | 'stack';
}

export const LabelValueRow = ({
  label,
  value,
  uppercaseLabel = false,
  tone = 'default',
  previousValue,
  layout = 'inline',
}: LabelValueRowProps) => {
  const showPrevious = previousValue != null && previousValue !== value;
  return (
    <div css={[ROW_STYLE, layout === 'stack' ? STACK_STYLE : INLINE_STYLE]}>
      <span css={[LABEL_STYLE, uppercaseLabel && LABEL_UPPERCASE_STYLE]}>{label}</span>
      <span css={[VALUE_STYLE, layout === 'inline' && VALUE_INLINE_STYLE, showPrevious && VALUE_UPDATED_STYLE]} style={{ color: VALUE_TONE_COLORS[tone] }}>
        {showPrevious ? <span css={PREVIOUS_VALUE_STYLE}>({presentWas(previousValue)})</span> : null}
        {showPrevious ? <span css={UPDATE_LABEL_STYLE}>update:</span> : null}
        {value}
      </span>
    </div>
  );
};
